import { FC, ReactNode } from 'react'
import styled from 'styled-components'
import { BD, Card, SEVERIDADE } from '../styles'
import { Stepper } from './Stepper'

const Lista = styled.div`
  display: flex;
  flex-direction: column;
`

const Linha = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 8px 0;
  cursor: pointer;
  & + & {
    border-top: 1px solid ${BD.grayBorder};
  }
`

const Caixa = styled.input`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  accent-color: ${BD.red};
`

const Texto = styled.span`
  flex-grow: 1;
  min-width: 0;
`

const Titulo = styled.span<{ $apagado?: boolean }>`
  display: block;
  font-size: 15px;
  font-weight: 500;
  color: ${({ $apagado }) => ($apagado ? BD.grayText : BD.black)};
`

const Detalhe = styled.span`
  display: block;
  font-size: 12px;
  color: ${BD.grayText};
`

const Cabecalho = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`

const Nome = styled.b`
  flex-grow: 1;
  min-width: 0;
  font-size: 15px;
`

const Referencia = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: ${BD.grayText};
`

const Controles = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`

const Nota = styled.div<{ $alerta?: boolean }>`
  margin-top: 8px;
  font-size: 12px;
  color: ${({ $alerta }) => ($alerta ? SEVERIDADE.atencao.texto : BD.grayText)};
  font-weight: ${({ $alerta }) => ($alerta ? 600 : 400)};
`

export interface ItemMarcacao {
  id: number
  titulo: string
  detalhe?: ReactNode
  marcada: boolean
}

export interface ListaMarcacaoProps {
  itens: ItemMarcacao[]
  aoAlternar: (id: number, marcada: boolean) => void
  disabled?: boolean
  vazio?: string
}

/**
 * OP-3619 — o checklist de campo.
 *
 * Uma linha por peça, alta o bastante para o polegar, com a caixa de marcação
 * de verdade (chega pelo teclado e anuncia o estado). Serve à contagem do
 * inventário e à coleta da remessa: as duas perguntam a mesma coisa — esta
 * peça está aqui agora? Quando gravar é decisão de quem chama: o inventário
 * grava a cada toque, a coleta junta e grava no fim.
 */
export const ListaMarcacao: FC<ListaMarcacaoProps> = ({ itens, aoAlternar, disabled, vazio }) => {
  if (itens.length === 0) {
    return <div style={{ fontSize: 13, color: BD.grayText, padding: '8px 0' }}>{vazio ?? 'Nada a marcar.'}</div>
  }
  return (
    <Lista>
      {itens.map((i) => (
        <Linha key={i.id}>
          <Caixa type="checkbox" checked={i.marcada} disabled={disabled} onChange={() => aoAlternar(i.id, !i.marcada)} />
          <Texto>
            <Titulo $apagado={!i.marcada}>{i.titulo}</Titulo>
            {i.detalhe && <Detalhe>{i.detalhe}</Detalhe>}
          </Texto>
        </Linha>
      ))}
    </Lista>
  )
}

export interface LinhaQuantidadeProps {
  titulo: string
  referencia?: ReactNode
  valor: string
  aoMudar: (valor: string) => void
  rotulo: string
  min?: number
  max?: number | null
  disabled?: boolean
  somenteLeitura?: boolean
  nota?: ReactNode
  alerta?: boolean
  acao?: ReactNode
}

/**
 * OP-3619 — a mesma pergunta do checklist, quando a resposta é um número.
 *
 * Controlada de ponta a ponta: recebe o valor, os limites e o callback, e não
 * sabe se está numa contagem ou numa coleta. `somenteLeitura` troca o stepper
 * pela nota, para a linha que já não aceita toque.
 */
export const LinhaQuantidade: FC<LinhaQuantidadeProps> = ({
  titulo, referencia, valor, aoMudar, rotulo, min, max, disabled, somenteLeitura, nota, alerta, acao,
}) => (
  <Card style={{ padding: 12 }}>
    <Cabecalho>
      <Nome>{titulo}</Nome>
      {referencia != null && <Referencia>{referencia}</Referencia>}
    </Cabecalho>
    {!somenteLeitura && (
      <Controles>
        <Stepper valor={valor} aoMudar={aoMudar} rotulo={rotulo} min={min} max={max} disabled={disabled} />
        {acao}
      </Controles>
    )}
    {nota != null && <Nota $alerta={alerta}>{nota}</Nota>}
  </Card>
)

export default ListaMarcacao
